/**
 * Genesis Shell — the immersive frame around every phase.
 *
 * Deliberately minimal chrome: a mono-uppercase workshop-eyebrow,
 * a very subtle progress dot cluster, and a full-bleed canvas.
 * No back button. No progress percentage. No breadcrumb.
 *
 * Dark, warm palette — the aim is to feel like being welcomed into
 * a private workshop, not marched through a SaaS wizard.
 */
import type { ReactNode } from "react";
import { PHASE_ORDER, type GenesisPhase } from "@/entities/genesis";

const PHASE_LABEL: Record<GenesisPhase, string> = {
  welcome: "Welcome",
  discovery: "Discovery",
  demo_department: "Meet Your Department",
  make_this_mine: "Make It Yours",
  intelly_conversation: "Tell Us About Your Business",
  logo: "Design a Mark",
  transformation: "Building",
  briefing: "First Briefing",
  complete: "Ready",
};

interface Props {
  phase: GenesisPhase;
  children: ReactNode;
}

export function GenesisShell({ phase, children }: Props): JSX.Element {
  const idx = PHASE_ORDER.indexOf(phase);

  return (
    <div className="min-h-screen bg-[#0d1310] text-[#f5efe6]" data-testid="genesis-shell">
      {/* Subtle grain overlay for warmth */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(#f5efe6 0.5px, transparent 0.5px)",
          backgroundSize: "3px 3px",
        }}
      />
      <header className="relative z-10 flex items-center justify-between px-6 pt-8 md:px-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#c7a25a]" data-testid="genesis-eyebrow">
          Marketing OS · Genesis
        </div>
        <div className="hidden items-center gap-1.5 md:flex" data-testid="genesis-phase-dots">
          {PHASE_ORDER.slice(0, -1).map((p, i) => {
            const done = i < idx;
            const active = i === idx;
            return (
              <span
                key={p}
                title={PHASE_LABEL[p]}
                className="h-1 rounded-full transition-all duration-500"
                style={{
                  width: active ? "28px" : "8px",
                  background: done ? "#c7a25a" : active ? "#f5efe6" : "rgba(245,239,230,0.15)",
                }}
              />
            );
          })}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#f5efe6]/60">
          {PHASE_LABEL[phase]}
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-6 md:px-10">
        {children}
      </main>
    </div>
  );
}
